import React, {
  forwardRef,
  ReactNode,
  TextareaHTMLAttributes,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
  useState,
} from "react";
import classNames from "classnames";

import "./CustomTextarea.scss";

interface CustomTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoGrow?: boolean;
  maxHeight?: number;
}

const MIN_HEIGHT = 40;
const MIN_VALID_SCROLL_HEIGHT = 20;
const RETRY_DELAY = 50;
const MAX_RETRIES = 10;

const CustomTextarea = forwardRef<HTMLTextAreaElement, CustomTextareaProps>(
  (props, ref) => {
    const {
      className,
      value,
      autoGrow,
      maxHeight,
      defaultValue,
      rows,
      ...restProps
    } = props;
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const [showScroll, setShowScroll] = useState(false);

    useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

    useLayoutEffect(() => {
      if (!internalRef.current) return;

      const textarea = internalRef.current;

      if (autoGrow) {
        let retryCount = 0;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let rafId: number | null = null;
        let isMounted = true;

        const adjustHeight = () => {
          if (!isMounted || !textarea) return;

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          textarea.style.height = `${MIN_HEIGHT}px`;

          const isVisible =
            textarea.offsetHeight > 0 && textarea.offsetWidth > 0;
          const scrollHeight = textarea.scrollHeight;

          const hasValidDimensions =
            isVisible && scrollHeight >= MIN_VALID_SCROLL_HEIGHT;

          if (!hasValidDimensions && retryCount < MAX_RETRIES) {
            retryCount++;
            timeoutId = setTimeout(adjustHeight, RETRY_DELAY);
            return;
          }

          if (maxHeight && scrollHeight > maxHeight) {
            textarea.style.height = `${maxHeight}px`;
            setShowScroll(true);
          } else if (scrollHeight > MIN_HEIGHT) {
            textarea.style.height = `${scrollHeight}px`;
            setShowScroll(false);
          } else {
            textarea.style.height = `${MIN_HEIGHT}px`;
            setShowScroll(false);
          }
        };

        adjustHeight();

        rafId = requestAnimationFrame(() => {
          retryCount = 0;
          adjustHeight();
        });

        const fallbackTimeoutId = setTimeout(() => {
          if (isMounted) {
            retryCount = 0;
            adjustHeight();
          }
        }, 100);

        return () => {
          isMounted = false;
          if (timeoutId) clearTimeout(timeoutId);
          if (rafId) cancelAnimationFrame(rafId);
          clearTimeout(fallbackTimeoutId);
        };
      } else {
        textarea.style.height = "";
        setShowScroll(false);
      }
    }, [value, autoGrow, maxHeight, defaultValue]);

    return (
      <textarea
        ref={internalRef}
        className={classNames(
          "ws-textarea",
          {
            "ws-textarea--auto-grow": autoGrow,
            "ws-textarea--with-scroll": showScroll,
          },
          className,
        )}
        value={value || ""}
        style={{ maxHeight }}
        rows={autoGrow ? undefined : rows}
        {...restProps}
      />
    );
  },
);

type LabelProps = {
  htmlFor: string;
  text: string | ReactNode;
};

export const CustomTextareaLabel: React.FC<LabelProps> = ({
  htmlFor,
  text,
}) => {
  return (
    <label htmlFor={htmlFor} className="ws-textarea-label">
      {text}
    </label>
  );
};

export default CustomTextarea;
